import { InjectionToken } from '@angular/core';
import { AppEnvironment } from '../../models/app/app-env.enum';

export interface PusherConfig {
  pusherKey: string;
  cluster: string;
  forceTLS: boolean;
  authEndpoint: string;
  environment: string;
  enableLogging: boolean;
}

const getEnvironmentPusherChannelPrefix = (env: Window['env'] | null): string => {
  const appEnv: string = env?.appEnv || env?.environment;
  const STAGING_DOMAIN: string = 'stage.yopa-dev.co.uk';

  if (appEnv === AppEnvironment.PRODUCTION || appEnv === AppEnvironment.PREPROD) {
    // Preprod/Prod: no prefix (empty string)
    return '';
  }

  if (
    appEnv === AppEnvironment.DOCKER
    || !(appEnv === AppEnvironment.STAGING && typeof window !== 'undefined')
  ) {
    return AppEnvironment.DOCKER;
  }

  const hostname: string = window.location.hostname;

  return hostname.endsWith(STAGING_DOMAIN)
    ? hostname.replace(STAGING_DOMAIN, '').replace(/\.$/, '')
    : AppEnvironment.DOCKER;
};

/**
 * Injection token for Pusher configuration
 * This allows different configuration strategies to be injected
 */
export const PUSHER_CONFIG: InjectionToken<PusherConfig> =
  new InjectionToken<PusherConfig>('pusher.config', {
    providedIn: 'root',
    factory: () => {
      // Default configuration factory
      // This will be used if no provider is explicitly registered
      const env: Window['env'] | null = typeof window !== 'undefined' && window.env ? window.env : null;

      const envpusherKey: string = env?.pusherKey || '';

      return {
        pusherKey: envpusherKey,
        cluster: 'eu',
        forceTLS: true,
        authEndpoint: '/broadcasting/auth',
        /**
         * IMPORTANT: on api-v2 the logic for forming the private channel name is following
         *  PREPROD & PROD -> NO PREFIXES (empty string)
         *  LOCAL -> 'docker'
         *  STAGING -> subdomain of stage.yopa-dev.co.uk e.g. jry-3271-1.stage.yopa-dev.co.uk
         *             will form 'jry-3271-1' prefix (must match Dispatcher::getEnvironmentName,
         *             which uses config('kimi.subdomain_url') on api-v2)
         */
        environment: getEnvironmentPusherChannelPrefix(env),
        enableLogging: !(env?.production || false),
      };
    },
  });

/**
 * Extended Window interface for environment configuration
 */
declare global {
  interface Window {
    env?: {
      pusherKey?: string;
      environment?: string;
      appEnv?: string;
      production?: boolean;
    };
  }
}
